import { divide } from 'tio-ui/math';
import { Divider } from 'tio-ui/components/utilities';
import { Header, Section, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { PhArrowLeft } from 'ember-phosphor-icons';
import { or } from 'tio-ui/utilities';
import Select from 'tio-reimagined/components/select';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import safeFormatDate from 'tio-common/helpers/safe-format-date';
import safeFormatNumber from 'tio-common/helpers/safe-format-number';
import Table from 'tio-reimagined/components/table';
import TioPageBreadcrumbs from 'tio-common/components/tio/page/breadcrumbs';
import type { SearchModelWith, TasProgramInstance } from 'tio-common/models/search';
import type IntlService from 'ember-intl/services/intl';
// This is for types. Anything we have custom may show an error.
import type StoreService from 'ember-data/store';
import type TasPaymentsController from 'tio-employee/controllers/authenticated/tas/payments';

export interface S {
  Args: {
    model: SearchModelWith<TasProgramInstance>;
    controller: TasPaymentsController;
  };
}

export default class AppTasPaymentsComponent extends Component<S> {
  @service declare intl: IntlService;
  @service declare store: StoreService;

  @tracked error?: Error;
  @tracked isLoading = false;
  @tracked selectedYear = '';

  get paidItems(): TasProgramInstance[] {
    return this.args.model.items.filter((record) => (record.paymentPaidAmount ?? 0) > 0);
  }

  get pendingItems(): TasProgramInstance[] {
    return this.args.model.items.filter((record) => (record.paymentPendingAmount ?? 0) > 0);
  }

  get totals() {
    let paid = 0;
    let pending = 0;
    for (const record of this.args.model.items) {
      paid += record.paymentPaidAmount ?? 0;
      pending += record.paymentPendingAmount ?? 0;
    }
    return { paid, pending };
  }

  get paidTotal() {
    return this.totals.paid;
  }

  get pendingTotal() {
    return this.totals.pending;
  }

  currentYear = new Date().getFullYear().toString();

  get yearOptions() {
    const beginYear: number = Number(this.currentYear) - 7;
    return Array.from({ length: 9 }, (_, i) => `${beginYear + i - 1} - ${beginYear + i}`);
  }

  setSelectedYear = (year: string | null) => {
    //if year is null, it means the user has cleared the year filter and an empty string should be set
    this.selectedYear = year || '';
    this.args.controller.paymentYear = year ? year.split(' - ')[1] || '' : '';
  };

  <template>
    <VStack>
      <TioPageBreadcrumbs class="flex items-center" as |b|>
        <PhArrowLeft @size="16" class="leading-none" />
        <b.crumb @route="authenticated.tas.dashboard" @label={{t "tuition_assistance.default"}} />
      </TioPageBreadcrumbs>
      <Header>
        <div class="flex justify-between">
          <div class="self-center">
            {{t "tuition_assistance.dashboard.payment_activity.payment_details"}}
          </div>
          <div class="mr-4">
            <Select
              @label={{t
                "tuition_assistance.dashboard.payment_activity.filter_all_records_by_year"
              }}
              @selected={{this.selectedYear}}
              @items={{this.yearOptions}}
              @onChange={{this.setSelectedYear}}
              @clearable={{true}}
              @placeholder="All Payments"
            />
          </div>
        </div>
      </Header>

      <Section>
        <:body>
          <div class="mb-3">
            {{#if this.pendingItems.length}}
              <h1 class="text-sm font-semibold uppercase mb-3">
                {{t "tuition_assistance.dashboard.pending_payment_details.title"}}
              </h1>
              <Table @isLoading={{false}}>
                <:thead as |options|>
                  <options.tr>
                    <options.th>
                      {{t "tuition_assistance.dashboard.pending_payment_details.application_name"}}
                    </options.th>
                    <options.th>
                      {{t "tuition_assistance.dashboard.pending_payment_details.expected_amount"}}
                    </options.th>
                  </options.tr>
                </:thead>
                <:tbody as |options|>
                  {{#each this.pendingItems as |record|}}
                    <options.tr>
                      <options.td>
                        <LinkTo
                          @route="authenticated.tas.applications.show"
                          @model={{record.tasApplicationIdKeyword}}
                          data-test={{record.tasApplicationIdKeyword}}
                        >
                          {{record.tasApplicationIdKeyword}}
                        </LinkTo>
                      </options.td>
                      <options.td>
                        {{safeFormatNumber
                          (divide (or record.paymentPendingAmount 0) 100)
                          style="currency"
                          currency="USD"
                        }}
                      </options.td>
                    </options.tr>
                  {{/each}}
                </:tbody>
              </Table>
              <Divider class="my-16" />
            {{/if}}
          </div>

          <div>
            <h1 class="text-sm font-semibold uppercase mb-3">
              {{t "tuition_assistance.dashboard.payment_details.title"}}
            </h1>
            {{#if this.paidItems.length}}
              <div class="mb-5">
                <Table @isLoading={{false}}>
                  <:thead as |options|>
                    <options.tr>
                      <options.th>
                        {{t "tuition_assistance.dashboard.payment_details.application_name"}}
                      </options.th>
                      <options.th>
                        {{t "tuition_assistance.dashboard.payment_details.payment_date"}}
                      </options.th>
                      <options.th>
                        {{t "tuition_assistance.dashboard.payment_details.payment_amount"}}
                      </options.th>
                    </options.tr>
                  </:thead>
                  <:tbody as |options|>
                    {{#each this.paidItems as |record|}}
                      <options.tr>
                        <options.td>
                          <LinkTo
                            @route="authenticated.tas.applications.show"
                            @model={{record.tasApplicationIdKeyword}}
                            data-test={{record.tasApplicationIdKeyword}}
                          >
                            {{record.tasApplicationIdKeyword}}
                          </LinkTo>
                        </options.td>
                        {{#if record.paymentLastDate}}
                          <options.td>{{safeFormatDate
                              record.paymentLastDate
                              month="2-digit"
                              day="2-digit"
                              year="numeric"
                            }}</options.td>
                        {{else}}
                          <options.td />
                        {{/if}}
                        <options.td>
                          {{safeFormatNumber
                            (divide (or record.paymentPaidAmount 0) 100)
                            style="currency"
                            currency="USD"
                          }}
                        </options.td>
                      </options.tr>
                    {{/each}}
                  </:tbody>
                </Table>
              </div>
            {{else}}
              <div class="">
                {{t "tuition_assistance.dashboard.payment_activity.no_records_found"}}
              </div>
            {{/if}}
            <div class="flex my-2 text-lg font-semibold text-white">
              <div class="flex justify-between py-2 px-8 mb-5 bg-tio-gray-400 w-full">
                <div>
                  {{t "tuition_assistance.dashboard.payment_activity.total"}}
                </div>
                <div>
                  {{safeFormatNumber (divide this.paidTotal 100) style="currency" currency="USD"}}
                </div>
              </div>
            </div>
          </div>
        </:body>
      </Section>
    </VStack>
  </template>
}
